import { Paper } from '@mui/material'
import moment from 'moment'
import React, { useMemo } from 'react'
import { DateRangeValue, EMobilityState } from '../EMobilityChargingSessions'
import { ChargingSessionModel } from '../charging-session-models'
import { ChargingSessionChart } from './ChargingSessionChart'

const CHART_DATE_FORMAT = 'YYYY-MM-DD'

/**
 * We transform incoming charging sessions array to this data structure.
 *
 * This data structure is chosen so that each date displays a single vertically stacked bar
 * which consists of several smaller bars, each related to a single wallbox inside the "wallboxes"
 * array.
 */
type ChartDataEntry = {
  date: string
  wallboxes: {
    [id: string]: {
      id: string
      deviceName: string
      grid: number
      solar: number
    }
  }
}

interface ChargingSessionChartContainerProps {
  installationId: number
  chargingSessions: ChargingSessionModel[]
  displayEnergyAll: boolean
  dateRange: DateRangeValue | undefined
  state: EMobilityState
  toggleHiddenWallbox: (wallboxType: 'grid' | 'solar', wallboxId: string) => void
}

/**
 * Chart container component for emobility charging sessions
 * requires charging sessions and total energy from sessions
 * it holds wallbox stat cards above chart
 * it holds chart bars
 * It is active on all screens
 */
export const ChargingSessionChartContainer: React.FC<ChargingSessionChartContainerProps> = ({
  installationId,
  chargingSessions,
  displayEnergyAll,
  dateRange,
  state,
  toggleHiddenWallbox,
}) => {
  const chartData = useMemo(() => {
    // first, filter for sessions with actual energy data
    const chargingSessionsWithChargeEnergy = chargingSessions.filter(session => session.energyAll > 0 || session.energySolar > 0)

    // then, remove "time" extension from datetime string to condense sessions of the same day, regardless of exact time
    const chartDataByDate = chargingSessionsWithChargeEnergy.map(session => ({
      date: moment.utc(session.startAt).format(CHART_DATE_FORMAT),
      wallboxes: chargingSessionsWithChargeEnergy
        .filter(innerSession => innerSession.startAt === session.startAt)
        .map(innerSession => ({
          id: innerSession.wallboxID,
          deviceName: innerSession.deviceName,
          grid: Math.max(innerSession.energyAll - innerSession.energySolar, 0),
          solar: Math.max(innerSession.energySolar, 0),
        })),
    }))

    const energySumByDate: Record<string, ChartDataEntry> = {}

    // if date entry already exists, add energy to existing sum for that specific day, otherwise create new date entry
    chartDataByDate.forEach(item => {
      if (!energySumByDate[item.date]) {
        energySumByDate[item.date] = {
          date: item.date,
          wallboxes: {},
        }
      }

      item.wallboxes.forEach(wallbox => {
        /** Check if the wallbox with the same ID already exists */
        const existingWallbox = energySumByDate[item.date].wallboxes[wallbox.id]

        if (existingWallbox) {
          /** If wallbox exists, add the values */
          existingWallbox.grid += wallbox.grid
          existingWallbox.solar += wallbox.solar
        } else {
          /** If the wallbox doesn't exist, push a new entry */
          energySumByDate[item.date].wallboxes[wallbox.id] = wallbox
        }
      })
    })

    /**
     * Since chartDataByDate array only consists of days which contain charge energy, e.g.
     * for 12.06.2023, then 15.06.2023, then 10.10.2023, we need to fill the gaps between these
     * dates with empty chart data so that the x-axis ticks are placed proportionally.
     */
    if (chartDataByDate.length > 0) {
      /* find min and max date which will be displayed in the chart */
      const minDate = dateRange
        ? moment(dateRange.startDate).startOf('day')
        : chartDataByDate.reduce((min, p) => (moment(p.date).isBefore(min) ? p.date : min), chartDataByDate[0].date)
      const maxDate = dateRange
        ? moment(dateRange.endDate).startOf('day')
        : chartDataByDate.reduce((min, p) => (moment(p.date).isAfter(min) ? p.date : min), chartDataByDate[0].date)

      // then fill with empty data for missing dates between min and max date, e.g. to plot all 31 days of March when "current month" option is selected
      for (let m = moment(minDate); m.isSameOrBefore(moment(maxDate), 'day'); m.add(1, 'days')) {
        const date = m.format(CHART_DATE_FORMAT)

        if (!energySumByDate[date]) {
          energySumByDate[date] = {
            date,
            wallboxes: {}, // Add an empty wallboxes array for each date
          }
        }
      }
    }

    const sortedEnergySums = Object.values(energySumByDate).sort((aStartDate, bStartDate) =>
      moment(aStartDate.date, CHART_DATE_FORMAT).isBefore(moment(bStartDate.date, CHART_DATE_FORMAT)) ? -1 : 1
    )

    /*
     * We round and display values with 2 decimal digits
     * Values under 0.005 are displayed in chart bar but when we round it for dynamic chart legend value is 0.00
     * We are displaying 0 for values under 0.005 for both chart bar and dynamic legend to stay consistence
     */
    for (const energyEntry of sortedEnergySums) {
      for (const wallbox of Object.values(energyEntry.wallboxes)) {
        wallbox.grid = Math.abs(wallbox.grid / 1000) < 0.005 ? 0 : wallbox.grid / 1000
        wallbox.solar = Math.abs(wallbox.solar / 1000) < 0.005 ? 0 : wallbox.solar / 1000
      }
    }

    return sortedEnergySums
  }, [chargingSessions, dateRange])

  return (
    <Paper
      sx={{
        pt: 2,
        pb: 6,
        pr: 2,
      }}
    >
      <ChargingSessionChart
        displayEnergyAll={displayEnergyAll}
        data={chartData}
        state={state}
        installationId={installationId}
        toggleHiddenWallbox={toggleHiddenWallbox}
      />
    </Paper>
  )
}
